import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Compass, Handshake, Network, Megaphone, Users } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/lib/images";
import Reveal from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];
const ICONS = [Compass, Handshake, Network, Megaphone, Users];

export default function Servizi() {
  const { t } = useLang();

  return (
    <main>
      {/* hero */}
      <section className="relative overflow-hidden bg-night" data-testid="services-hero">
        <div className="absolute inset-0">
          <img
            src={IMAGES.arch}
            alt=""
            className="img-warm h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-40 lg:px-10 lg:pb-28 lg:pt-48">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold"
            data-testid="services-eyebrow"
          >
            <span className="inline-block h-[2px] w-8 bg-terra" />
            {t.services.eyebrow}
          </motion.p>
          <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.08] tracking-tight text-sand sm:text-6xl">
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, ease: EASE, delay: 0.35 }}
                data-testid="services-title"
              >
                {t.services.title}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-sand/80 md:text-lg"
            data-testid="services-sub"
          >
            {t.services.sub}
          </motion.p>
        </div>
      </section>

      {/* service rows */}
      <section className="bg-sand" data-testid="services-list">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={i} delay={i * 0.04}>
                <div
                  className="group grid gap-6 border-t border-linec py-10 last:border-b lg:grid-cols-12 lg:items-start lg:gap-8 lg:py-14"
                  data-testid={`service-row-${i + 1}`}
                >
                  <div className="flex items-center gap-5 lg:col-span-2">
                    <span className="font-mono text-xs text-terra">0{i + 1}</span>
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-linec bg-white text-terra transition-all duration-300 group-hover:border-terra group-hover:bg-terra group-hover:text-sand">
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="lg:col-span-5">
                    <h2 className="font-serif text-2xl font-medium text-night transition-colors duration-300 group-hover:text-terra md:text-3xl">
                      {s.title}
                    </h2>
                  </div>
                  <p className="text-base leading-relaxed text-mutedx lg:col-span-5">
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}

          <Reveal>
            <p className="mt-10 max-w-2xl font-serif text-xl italic leading-relaxed text-night/70" data-testid="services-note">
              {t.services.note}
            </p>
          </Reveal>
        </div>
      </section>

      {/* process timeline */}
      <section className="bg-night" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
              <span className="inline-block h-[2px] w-8 bg-terra" />
              {t.services.process.eyebrow}
            </p>
            <h2 className="mt-5 font-serif text-3xl font-medium text-sand sm:text-4xl">
              {t.services.process.title}
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-10 md:grid-cols-4">
            {t.services.process.steps.map((step, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="relative border-l border-sand/15 pl-6" data-testid={`process-step-${i + 1}`}>
                  <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-terra" />
                  <span className="font-mono text-xs text-gold">{step.n}</span>
                  <h3 className="mt-3 font-serif text-2xl font-medium text-sand">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-sand/60">
                    {step.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-20 flex flex-wrap items-center gap-6 rounded-2xl border border-sand/15 bg-nightlight/60 p-8">
              <p className="max-w-md font-serif text-2xl italic text-sand">
                {t.services.cta}
              </p>
              <Link
                to="/contatti"
                data-testid="services-cta-btn"
                className="group ml-auto inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-bold text-sand transition-all duration-200 hover:-translate-y-0.5 hover:bg-terra-dark"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
